import { BorderCrossing, OvernightStay, PitStopStay, RoutePoint, WeatherDelay } from "../../generated/gpb/api/route";
import { BackHand, Bedtime, LunchDining, Place, SportsScore } from "@mui/icons-material";
import { faIconStyle, iconStyle } from "./GeoViewStyle";
import {
    BORDER_CROSSING_DESCRIPTION,
    BORDER_CROSSING_DISCLAIMER,
    BORDER_CROSSING_STRING,
    END_STRING,
    OVERNIGHT_STOP_HEADER,
    PIT_STOP_STRING,
    START_STRING,
    WAYPOINT_STOP_HEADER,
    WEATHER_DELAY_DESCRIPTION,
    WEATHER_DELAY_STRING
} from "../UserVisibleStrings";
import { formatOvernightTime, formatPitStopTime } from "./geoUtils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowDownUpAcrossLine, faMapPin } from "@fortawesome/sharp-duotone-light-svg-icons";
import { getPlaceName } from "../location/LocationSelectModal";

interface MarkerIconProps
{
    type: MarkerType;
    scale: number;
    isRVIcon?: boolean;
    overrideSize?: boolean;
}

export interface MarkerData
{
    location: RoutePoint | undefined;
    isPlanned: boolean;
    isFinalStop: boolean;
    icon: JSX.Element;
    key: string;
    stopType: string;
    description: string;
    durationString: string;
}

interface MarkerBuilderParams
{
    location: RoutePoint | undefined;
    index: number;
    iconScale: number;
    type: MarkerType;
    isPlanned: boolean;
    isFinalStop: boolean;
    text: string;
    descriptionGenerator?: () => string;
    durationString?: string;
    isFirst?: boolean;
}

export enum MarkerType
    {
    PIT_STOP = "PIT_STOP",
    BORDER_CROSSING = "BORDER_CROSSING",
    OVERNIGHT_STAY_RECOMMENDED = "OVERNIGHT_STAY_RECOMMENDED",
    OVERNIGHT_STAY_REQUESTED = "OVERNIGHT_STAY_REQUESTED",
    WEATHER_DELAY = "WEATHER_DELAY",
    START = "START",
    END = "END",
    NORMAL = "NORMAL"
}
export function markerTypeDisplayString( type: MarkerType ): string
{
    switch ( type )
    {
        case MarkerType.PIT_STOP: return PIT_STOP_STRING;
        case MarkerType.OVERNIGHT_STAY_REQUESTED: return WAYPOINT_STOP_HEADER;
        case MarkerType.OVERNIGHT_STAY_RECOMMENDED: return OVERNIGHT_STOP_HEADER;
        case MarkerType.BORDER_CROSSING: return BORDER_CROSSING_STRING;
        case MarkerType.WEATHER_DELAY: return WEATHER_DELAY_STRING;
        case MarkerType.START: return START_STRING;
        case MarkerType.END: return END_STRING;
        default:
        case MarkerType.NORMAL:
            return "default";
    }
}

/**
 * Generate a waypoint icon based on its type.
 * @param {MarkerIconProps} props -- Details of the waypoint
 */
export const getWaypointIcon = ( { type, scale, isRVIcon = false }: MarkerIconProps ): JSX.Element =>
{
    const commonStyle = { ...iconStyle, scale: `${scale}%`, color: isRVIcon ? "red" : "white" };

    switch ( type )
    {
        case MarkerType.PIT_STOP:
            return <LunchDining style = {commonStyle}/>;
        case MarkerType.BORDER_CROSSING:
            return <FontAwesomeIcon icon = {faArrowDownUpAcrossLine} size = "lg" fixedWidth style = {faIconStyle}/>;
        case MarkerType.OVERNIGHT_STAY_RECOMMENDED:
            return <Bedtime style = {commonStyle}/>;
        case MarkerType.OVERNIGHT_STAY_REQUESTED:
            return <Place style = {commonStyle}/>;
        case MarkerType.START:
            return <FontAwesomeIcon icon = {faMapPin} size = "lg" fixedWidth style = {faIconStyle}/>;
        case MarkerType.END:
            return <SportsScore style = {commonStyle}/>;
        case MarkerType.WEATHER_DELAY:
            return <BackHand style = {{ ...iconStyle, scale: `${scale * 0.7}%` }}/>;
        case MarkerType.NORMAL:
        default:
            return <Place style = {commonStyle}/>;
    }
};

export function fetchAndSetPlaceNames<T extends { location?: { latitude?: number, longitude?: number } }>(
    items: Array<T>,
    setLocationNames: ( names: Array<string | undefined> ) => void
)
{
    async function fetchPlaceNames()
    {
        try
        {
            const placeNamePromises = items.map( item => getPlaceName( item.location?.latitude, item.location?.longitude ) );
            const names = await Promise.all( placeNamePromises );
            setLocationNames( names );
        }
        catch ( err )
        {
            console.error( "Error fetching placenames in reverse geocoding:", err );
        }
    }

    fetchPlaceNames().catch( err => console.error( "Error fetching placenames in reverse geocoding:", err ) );
}



export const buildMarkerData = ( {
    location,
    index,
    iconScale,
    type,
    isPlanned,
    isFinalStop,
    text,
    descriptionGenerator,
    durationString,
    isFirst = false
}: MarkerBuilderParams ): MarkerData => ( {
    location,
    isPlanned,
    isFinalStop,
    icon: getWaypointIcon( { type, scale: iconScale * 0.7 } ),
    key: isFirst ? "startMarker" : `${text.toLowerCase()}Marker${index}`,
    stopType: text,
    description: descriptionGenerator ? descriptionGenerator() : "",
    durationString: durationString !== undefined ? durationString : "",
} );
export function getOvernightType( isPlanned: boolean )
{
    return isPlanned ? MarkerType.OVERNIGHT_STAY_REQUESTED : MarkerType.OVERNIGHT_STAY_RECOMMENDED;
}
export const getStayMarkerData = (
    stay: OvernightStay, index: number, iconScale: number, locationName: string | undefined
): MarkerData => buildMarkerData( {
    location: stay.location,
    index,
    iconScale,
    type: getOvernightType( stay.isPlanned ),
    isPlanned: stay.isPlanned,
    isFinalStop: true,
    text: markerTypeDisplayString( getOvernightType( stay.isPlanned ) ),
    descriptionGenerator: () => locationName !== undefined
        ? `${locationName}.`
        : "",
    durationString: `${formatOvernightTime( stay.startTime?.valueOf(), stay.endTime?.valueOf() )}`
} );

export const getBorderCrossingMarkerData = (
    crossing: BorderCrossing, index: number, iconScale: number, locationName: string | undefined
): MarkerData => buildMarkerData( {
    location: crossing.location,
    index,
    iconScale,
    type: MarkerType.BORDER_CROSSING,
    isPlanned: true,
    isFinalStop: false,
    text: markerTypeDisplayString( MarkerType.BORDER_CROSSING ),
    descriptionGenerator: () => locationName !== undefined
        ? `${BORDER_CROSSING_DESCRIPTION} ${locationName} to last ${formatPitStopTime( crossing.startTime?.valueOf(), crossing.endTime?.valueOf() )}. ${BORDER_CROSSING_DISCLAIMER}`
        : "",
    durationString: `${formatPitStopTime( crossing.startTime?.valueOf(), crossing.endTime?.valueOf() )}`
} );

export const getPitStopMarkerData = (
    pitStop: PitStopStay, index: number, iconScale: number, locationName: string | undefined
): MarkerData => buildMarkerData( {
    location: pitStop.location,
    index,
    iconScale,
    type: MarkerType.PIT_STOP,
    isPlanned: true,
    isFinalStop: false,
    text: markerTypeDisplayString( MarkerType.PIT_STOP ),
    descriptionGenerator: () => locationName !== undefined
        ? `${locationName}.`
        : "",
    durationString: `${formatPitStopTime( pitStop.startTime?.valueOf(), pitStop.endTime?.valueOf() )}`
} );

export const getDelayMarkerData = (
    delay: WeatherDelay, index: number, iconScale: number, locationName: string | undefined
): MarkerData => buildMarkerData( {
    location: delay.location,
    index: index,
    iconScale: iconScale,
    type: MarkerType.WEATHER_DELAY,
    isPlanned: false,
    isFinalStop: false,
    text: markerTypeDisplayString( MarkerType.WEATHER_DELAY ),
    descriptionGenerator: ( ) => ( locationName !== undefined
        ? `${WEATHER_DELAY_DESCRIPTION} ${locationName}`
        : "" ),
    durationString: `${formatPitStopTime( delay.startTime?.valueOf(), delay.endTime?.valueOf() )}`,
    isFirst: false
} );

export const getFirstOrFinalMarker = (
    lastLocation: RoutePoint, stayCount: number, iconScale: number, isFirst = false
): MarkerData => buildMarkerData( {
    location: lastLocation,
    index: stayCount + 1,
    iconScale,
    type: isFirst ? MarkerType.START : MarkerType.END,
    isPlanned: true,
    isFinalStop: true,
    isFirst,
    text: markerTypeDisplayString( isFirst ? MarkerType.START : MarkerType.END ),
} );
